import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Camera, ClipboardCheck, Printer } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { wibTime } from "./ManagerRequests";

const B = process.env.REACT_APP_BACKEND_URL;
const TONE = { MENUNGGU_SERAH: "bg-amber-100 text-amber-900", MENUNGGU_TERIMA: "bg-sky-100 text-sky-900", SELESAI: "bg-emerald-100 text-emerald-800" };
const PART = { SERAH: "Foto penyerahan", TERIMA: "Foto penerimaan", PT_SJI: "Foto PT SJI" };

const Photos = ({ h, part }) => {
  const list = h.photos.filter((p) => p.part === part);
  if (!list.length) return null;
  return (
    <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid={`handover-photos-${part}-${h.id}`}>
      {list.map((p) => (
        <a key={p.id} href={`${B}/api/handovers/${h.id}/photos/${p.id}`} target="_blank" rel="noreferrer" data-testid={`handover-photo-${p.id}`}>
          {/hei[cf]$/i.test(p.contentType || "")
            ? <span className="grid aspect-square w-full place-items-center rounded-lg border bg-slate-50 p-1 text-center text-[10px] text-slate-500">Foto HEIC — ketuk untuk unduh</span>
            : <img src={`${B}/api/handovers/${h.id}/photos/${p.id}`} alt={p.name} loading="lazy" className="aspect-square w-full rounded-lg border object-cover" />}</a>))}
    </div>
  );
};

const Part = ({ title, part, h, photoPart }) => (part ? (
  <div className="rounded-lg bg-slate-50 p-3 text-sm" data-testid={`handover-part-${photoPart}-${h.id}`}>
    <p className="text-xs font-bold text-slate-600">{title} · {part.byName}{part.onBehalf ? " (diisi PT SJI)" : ""} · {wibTime(part.at)}</p>
    <p className="mt-1 whitespace-pre-line text-slate-800 break-words">{part.note}</p>
    <Photos h={h} part={photoPart} />
  </div>) : null);

const Form = ({ h, kind, onDone }) => {
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const cfg = {
    submit: ["Catatan penyerahan", "Kondisi kebun, jumlah pohon, alat/bahan yang diserahkan, pekerjaan yang belum selesai…", "Kirim Catatan Penyerahan"],
    receive: ["Konfirmasi penerimaan", "Kondisi kebun saat diterima, kesesuaian dengan catatan penyerahan, temuan…", "Konfirmasi Penerimaan"],
    notes: ["Catatan PT SJI", "Catatan tambahan PT SJI…", "Tambah Catatan"],
  }[kind];
  const send = async () => {
    const fd = new FormData();
    fd.append("note", note);
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try { await api.post(`/handovers/${h.id}/${kind}`, fd); toast.success("Tersimpan"); setNote(""); setFiles([]); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); onDone(); }
  };
  const min = kind === "notes" ? 3 : 10;
  return (
    <div className="space-y-2 rounded-lg border border-dashed p-3" data-testid={`handover-form-${kind}-${h.id}`}>
      <p className="text-xs font-bold text-slate-700">{cfg[0]}{kind === "submit" && h.can.onBehalf ? " (atas nama Pengelola lama)" : ""}</p>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={cfg[1]} data-testid={`handover-note-${kind}-${h.id}`} />
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-xs font-semibold hover:bg-slate-50" data-testid={`handover-files-btn-${kind}-${h.id}`}>
          <Camera className="h-4 w-4" />{files.length ? `${files.length} foto dipilih` : "Pilih Foto"}
          <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden"
            onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 10))} data-testid={`handover-files-${kind}-${h.id}`} /></label>
        <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" disabled={busy || note.trim().length < min} onClick={send} data-testid={`handover-send-${kind}-${h.id}`}>
          {busy ? "Menyimpan…" : cfg[2]}</Button>
      </div>
      {files.length > 0 && <p className="text-[11px] text-slate-600 break-words" data-testid={`handover-files-names-${kind}-${h.id}`}>{files.map((f) => f.name).join(", ")}</p>}
      <p className="text-[11px] text-slate-500">Maks 10 foto (JPG, PNG, WEBP, HEIC; 15 MB per foto). Catatan minimal {min} karakter.</p>
    </div>
  );
};

export const HandoverCard = ({ h, reload }) => (
  <li className="rounded-xl border bg-white p-4 space-y-3" data-testid={`handover-${h.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-bold text-slate-900 break-words">{h.projectCode} · {h.projectName}</p>
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600">{h.from.managerName} <ArrowRight className="h-3 w-3" /> {h.to.managerName} · dimulai {wibTime(h.createdAt)}</p>
      </div>
      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${TONE[h.status]}`} data-testid={`handover-status-${h.id}`}>{h.statusLabel}</span>
    </div>
    <a href={`${B}/api/handovers/${h.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-800 px-3 text-xs font-bold text-emerald-800 hover:bg-emerald-50" data-testid={`handover-pdf-${h.id}`}>
      <Printer className="h-4 w-4" />{h.status === "SELESAI" ? "Cetak Berita Acara (PDF)" : "Lihat Draf Berita Acara (PDF)"}</a>
    <Part title="Penyerahan" part={h.handover} h={h} photoPart="SERAH" />
    <Part title="Penerimaan" part={h.receipt} h={h} photoPart="TERIMA" />
    {h.adminNotes.map((n) => (
      <div key={n.id} className="rounded-lg bg-emerald-50 p-3 text-sm" data-testid={`handover-admin-note-${n.id}`}>
        <p className="text-xs font-bold text-emerald-900">Catatan PT SJI · {n.byName} · {wibTime(n.at)}</p>
        <p className="mt-1 whitespace-pre-line break-words">{n.note}</p></div>))}
    <Photos h={h} part="PT_SJI" />
    {h.can.submit && <Form h={h} kind="submit" onDone={reload} />}
    {h.can.receive && <Form h={h} kind="receive" onDone={reload} />}
    {h.can.note && <Form h={h} kind="notes" onDone={reload} />}
  </li>
);

export const HandoverList = ({ url, title = "Serah Terima Kebun", testid = "handover-list", refreshKey }) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { api.get(url).then((r) => setRows(r.data)).catch(() => setRows([])); }, [url]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!rows?.length) return null;
  return (
    <section className="space-y-3" data-testid={testid}>
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><ClipboardCheck className="h-4 w-4 text-emerald-800" />{title}</p>
      <ul className="space-y-3">{rows.map((h) => <HandoverCard key={h.id} h={h} reload={load} />)}</ul>
    </section>
  );
};
